import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, List, X } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_exfoliating-bars/artifacts/xh2ysc0y_bd6a90e97_simplelogo.png";

const navLinks = [
  { label: "Products", href: "#products" },
  { label: "Our Story", href: "#story" },
  { label: "Reviews", href: "#reviews" },
  { label: "Feedback", href: "#feedback" },
  { label: "FAQ", href: "#faq" },
  { label: "Raffle", href: "/raffle" },
];

export default function Navbar({ cartCount, onCartClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav shadow-sm" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3" data-testid="navbar-logo">
            <img src={LOGO_URL} alt="Dermasand" className="h-14 w-auto" />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  if (link.href.startsWith('/')) {
                    e.preventDefault();
                    navigate(link.href);
                  }
                }}
                className="font-accent text-sm font-medium tracking-wide uppercase transition-colors duration-200"
                style={{ color: '#9A9A9A' }}
                onMouseEnter={(e) => e.target.style.color = '#FF5A36'}
                onMouseLeave={(e) => e.target.style.color = '#9A9A9A'}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Cart + Mobile Toggle */}
          <div className="flex items-center gap-4">
            <button
              onClick={onCartClick}
              className="relative p-2 transition-colors duration-200"
              style={{ color: '#F0EDE5' }}
              data-testid="cart-button"
            >
              <ShoppingBag size={24} weight="regular" />
              {cartCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-bold text-white rounded-full"
                  style={{ backgroundColor: '#FF5A36' }}
                  data-testid="cart-count"
                >
                  {cartCount}
                </span>
              )}
            </button>

            {/* Mobile menu toggle */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
              style={{ color: '#F0EDE5' }}
            >
              {mobileOpen ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden border-t"
          style={{ backgroundColor: 'rgba(12,12,12,0.95)', borderColor: '#2A2A2A' }}
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="font-accent text-base font-medium tracking-wide"
                style={{ color: '#F0EDE5' }}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </a>
            ))}
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
}
